import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Id } from '@cvx/_generated/dataModel'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@rekrytering/ui/components/select'
import { useMutation, useQuery } from '@tanstack/react-query'

type Props = {
  readonly candidateId: Id<'candidates'>
}

export const CandidateTagsEditor = ({ candidateId }: Props) => {
  const { data: allTags = [] } = useQuery(
    convexQuery(api.features.candidates.tags.listMyTags, {}),
  )

  const { data: candidateTags = [] } = useQuery(
    convexQuery(api.features.candidates.tags.listTagsForMyCandidate, {
      candidateId,
    }),
  )

  const { mutateAsync: addTag } = useMutation({
    mutationFn: useConvexMutation(
      api.features.candidates.tags.addTagToMyCandidate,
    ),
  })

  const { mutateAsync: removeTag } = useMutation({
    mutationFn: useConvexMutation(
      api.features.candidates.tags.removeTagFromMyCandidate,
    ),
  })

  const assignedTagIds = new Set(candidateTags.map((t) => t._id))
  const availableTags = allTags.filter((t) => !assignedTagIds.has(t._id))

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-sm font-medium text-primary">Tags</h3>

      <div className="flex flex-wrap gap-1.5">
        {candidateTags.map((tag) => (
          <span
            key={tag._id}
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
            style={{ backgroundColor: `${tag.color}20`, color: tag.color }}
          >
            {tag.label}
            <button
              type="button"
              className="hover:opacity-70"
              onClick={() =>
                void removeTag({
                  candidateId,
                  tagId: tag._id as Id<'tags'>,
                })
              }
            >
              ×
            </button>
          </span>
        ))}
        {candidateTags.length === 0 && (
          <span className="text-xs text-primary/40">No tags assigned.</span>
        )}
      </div>

      {availableTags.length > 0 && (
        <Select
          value=""
          onValueChange={(value) =>
            void addTag({ candidateId, tagId: value as Id<'tags'> })
          }
        >
          <SelectTrigger className="h-7 w-40 text-xs">
            <SelectValue placeholder="Add tag..." />
          </SelectTrigger>
          <SelectContent>
            {availableTags.map((tag) => (
              <SelectItem key={tag._id} value={tag._id}>
                <span
                  className="inline-block rounded-full px-2 py-0.5 text-xs font-medium"
                  style={{
                    backgroundColor: `${tag.color}20`,
                    color: tag.color,
                  }}
                >
                  {tag.label}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  )
}
